import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Users, Check, X as XIcon, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * SupportIncoming — a sponsor's inbox of incoming support asks.
 *
 * Per directive: single decision only — Yes or No. No negotiation, no
 * threads, no counter-offers, no pricing.
 */
export default function SupportIncoming() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSponsor, setIsSponsor] = useState(false);
  const [sponsorName, setSponsorName] = useState('');
  const [deciding, setDeciding] = useState(null);

  const fetchInbox = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/support/requests/incoming`, { withCredentials: true });
      setRows(r.data?.requests || []);
      setIsSponsor(Boolean(r.data?.is_sponsor));
      setSponsorName(r.data?.sponsor_name || '');
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInbox(); }, []);

  const decide = async (requestId, decision) => {
    if (deciding) return;
    setDeciding(requestId);
    try {
      await axios.post(
        `${API}/api/support/requests/${requestId}/respond`,
        { decision },
        { withCredentials: true },
      );
      toast.success(decision === 'yes' ? 'Support applied.' : 'Declined.');
      fetchInbox();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not save decision.';
      toast.error(typeof msg === 'string' ? msg : 'Could not save decision.');
    } finally {
      setDeciding(null);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }} data-testid="support-incoming-page">
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-2xl mx-auto px-4 flex items-center h-14 gap-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 hover:opacity-70" aria-label="Back">
            <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
          </button>
          <h1 className="text-base font-semibold truncate flex-1" style={{ color: 'var(--fy-text-primary)' }}>
            Asks for support {sponsorName ? `— ${sponsorName}` : ''}
          </h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--fy-text-secondary)' }} />
          </div>
        ) : !isSponsor ? (
          <div className="py-10 text-center">
            <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
              This page is for sponsor accounts.
            </p>
          </div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center">
            <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
              No asks right now.
            </p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="support-incoming-list">
            {rows.map(r => (
              <li
                key={r.request_id}
                className="rounded-2xl p-4"
                style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
                data-testid={`support-incoming-item-${r.request_id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--lk-sage-muted)' }}>
                    <Users className="w-4 h-4" style={{ color: 'var(--lk-sage)' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-bold" style={{ color: 'var(--fy-text-primary)' }}>
                      {r.group_name}
                    </p>
                    {r.group_description && (
                      <p className="text-sm mt-1" style={{ color: 'var(--fy-text-secondary)' }}>
                        {r.group_description}
                      </p>
                    )}
                    {r.note && (
                      <p className="text-xs mt-2 italic" style={{ color: 'var(--fy-text-primary)' }}>
                        “{r.note}”
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    onClick={() => decide(r.request_id, 'yes')}
                    disabled={deciding === r.request_id}
                    className="flex-1 px-4 py-2.5 rounded-full text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                    style={{ backgroundColor: '#4F6D4A', color: '#FFFFFF' }}
                    data-testid={`support-yes-${r.request_id}`}
                  >
                    {deciding === r.request_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => decide(r.request_id, 'no')}
                    disabled={deciding === r.request_id}
                    className="flex-1 px-4 py-2.5 rounded-full text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                    style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}
                    data-testid={`support-no-${r.request_id}`}
                  >
                    <XIcon className="w-4 h-4" />
                    No
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
